import { useState, useMemo } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import { RatingBadgeChip, Section, SectionCard, SectionHeader } from '@/components/common';
import { RichTextEditor } from '@/components/common/RichTextEditor/RichTextEditor';
import { RichTextReadOnly } from '@/components/common/RichTextEditor/RichTextReadOnly';
import { useUpdatePerformanceSummary } from '@/queries/useAppraisalDetail';
import type { AppraisalDetailResponse } from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';
import { readOnlySectionVariant } from '@/auth/sectionReadOnlyStyle';

interface PerformanceSummarySectionProps {
  appraisalDetail: AppraisalDetailResponse;
  permissions: RecordAccess;
  validationTriggered?: boolean;
}

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').trim();

const errorWrapperSx = {
  border: '2px solid',
  borderColor: 'error.main',
  borderRadius: 1,
} as const;

export function PerformanceSummarySection({
  appraisalDetail,
  permissions,
  validationTriggered = false,
}: PerformanceSummarySectionProps) {
  const sectionAccess = permissions.sections.performanceSummary;
  const canEdit = sectionAccess?.access === 'edit';

  const updateMutation = useUpdatePerformanceSummary(String(appraisalDetail.id));

  // Local state for optimistic updates
  const [localSummary, setLocalSummary] = useState<string>(appraisalDetail.performanceSummary ?? '');

  // Track previous server values for sync on cache invalidation
  const serverKey = useMemo(
    () => JSON.stringify({ summary: appraisalDetail.performanceSummary }),
    [appraisalDetail.performanceSummary]
  );

  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    setLocalSummary(appraisalDetail.performanceSummary ?? '');
  }

  if (!sectionAccess || sectionAccess.access === 'none') {
    return null;
  }

  const handleSummaryBlur = () => {
    updateMutation.mutate({
      data: {
        performanceSummary: localSummary || null,
      },
      rowVersion: appraisalDetail.rowVersion,
    });
  };

  // #1089: Proposed Rating moved to "Notes to TC". Final Rating / TC decision checkboxes
  // appear only once FSR is ON (their columns stay empty until then).
  const summaryError = validationTriggered && canEdit && stripHtml(localSummary) === '';

  return (
    <Section>
      <SectionHeader error={summaryError}>PERFORMANCE SUMMARY</SectionHeader>

      <SectionCard error={summaryError}>
        {canEdit ? (
          <Box
            sx={summaryError ? errorWrapperSx : undefined}
            data-testid={summaryError ? 'field-error' : undefined}
          >
            <RichTextEditor
              content={localSummary}
              onChange={setLocalSummary}
              onBlur={handleSummaryBlur}
              placeholder="Type here performance summary..."
              aria-label="Performance Summary"
            />
          </Box>
        ) : (
          <RichTextReadOnly
            content={appraisalDetail.performanceSummary ?? ''}
            variant={readOnlySectionVariant('performanceSummary', permissions.role)}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 6, mt: 3, flexWrap: 'wrap' }}>
          {/* Final Rating — visible only after FSR has been turned ON in TC Run. */}
          {appraisalDetail.finalRating !== null && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Final Rating
              </Typography>
              <RatingBadgeChip code={appraisalDetail.finalRating} />
            </Box>
          )}

          {/* #1089 Q2: TC decision checkboxes follow the FSR toggle in Performance Summary
              (hidden while FSR is OFF). They still remain under "Notes & Actions for Collator".
              #1109: Check-in mirrors the same FSR gate + read-only pattern; shown only when
              its TC Actions flag was ticked on TC Run. */}
          {appraisalDetail.isFinalStaffingReview &&
            (appraisalDetail.promote || appraisalDetail.holdAtGrade || appraisalDetail.tcCheckIn) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {appraisalDetail.promote && (
                <FormControlLabel
                  control={<Checkbox checked disabled inputProps={{ 'aria-label': 'Promote' }} />}
                  label="Promote"
                />
              )}
              {appraisalDetail.holdAtGrade && (
                <FormControlLabel
                  control={<Checkbox checked disabled inputProps={{ 'aria-label': 'Hold at Grade' }} />}
                  label="Hold at Grade"
                />
              )}
              {appraisalDetail.tcCheckIn && (
                <FormControlLabel
                  control={<Checkbox checked disabled inputProps={{ 'aria-label': 'Check-in' }} />}
                  label="Check-in"
                />
              )}
            </Box>
          )}
        </Box>
      </SectionCard>
    </Section>
  );
}
